import { useEffect, useMemo, useRef, useState } from 'react';
import { addIncidentAction, createIncident, downloadIncidentReport, fetchIncidentById, fetchIncidents, updateIncident, uploadDashcam, uploadIncidentImage, uploadSupportingDocument, uploadTachograph } from '../../api/incidents';
import { fetch_all_vehicles } from '../../api/vehicles';
import { useAuthProvider } from '../../providers/AuthProvider';

const STATUS_OPTIONS = ['draft', 'submitted', 'under_review', 'action_required', 'closed'];

const formatIncidentDate = (value) => {
    if (!value) return 'Not submitted';

    try {
        return new Date(value).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return value;
    }
};

const formatBoolean = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return 'Not provided';
};

const sectionStyle = (theme) => ({
    marginBottom: 16,
    padding: 14,
    border: `1px solid ${theme === 'dark' ? '#444' : '#e5e7eb'}`,
    borderRadius: 8,
    background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc',
});

const sectionTitleStyle = (theme) => ({
    marginBottom: 10,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme === 'dark' ? '#d1d5db' : '#374151',
});

const detailGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
};

const ourDetailsGridStyle = {
    ...detailGridStyle,
    gridTemplateAreas: `
        "dashcam tachograph"
        "vehicle immediate"
    `,
};

const getInputStyle = (theme, disabled = false) => ({
    width: '100%',
    padding: 8,
    borderRadius: 6,
    border: `1px solid ${theme === 'dark' ? '#555' : '#cfd6de'}`,
    background: disabled
        ? (theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)')
        : (theme === 'dark' ? '#2f3640' : '#ffffff'),
    color: disabled
        ? (theme === 'dark' ? '#a8b0ba' : '#6b7280')
        : (theme === 'dark' ? '#f3f4f6' : '#111827'),
});

const getCheckboxCardStyle = (theme, checked) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 8,
    border: `1px solid ${checked ? '#1997eb' : theme === 'dark' ? '#555' : '#d1d5db'}`,
    background: checked
        ? 'rgba(25, 151, 235, 0.12)'
        : theme === 'dark'
            ? 'rgba(255,255,255,0.03)'
            : '#ffffff',
});

const IncidentsManagement = ({ theme, showAlert, allProfiles = [] }) => {
    const { permissions, isDev } = useAuthProvider();
    const canEdit = isDev || (permissions && permissions.perm_incidents_edit);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [incidents, setIncidents] = useState([]);
    const [selectedIncidentId, setSelectedIncidentId] = useState(null);
    const [incidentDetails, setIncidentDetails] = useState(null);
    const [isEditingImmediateActions, setIsEditingImmediateActions] = useState(false);
    const [isUploadingDashcam, setIsUploadingDashcam] = useState(false);
    const [dashcamUploadProgress, setDashcamUploadProgress] = useState(0);
    const [dashcamUploadPhase, setDashcamUploadPhase] = useState(''); // 'uploading' | 'saving' | ''
    const [isUploadingTachograph, setIsUploadingTachograph] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [isUploadingDocument, setIsUploadingDocument] = useState(false);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [allVehicles, setAllVehicles] = useState([]);
    const defaultCreateForm = {
        profile_id: '', vehicle_id: '', incident_type: '', incident_at: '',
        summary: '', driver_statement: '', location_text: '',
        weather_conditions: '', road_conditions: '',
        has_injury: false, injured_person_name: '', injury_details: '',
        emergency_services_called: false, police_attended: false, police_reference: '',
        witness_details: '', other_party_name: '', other_party_company: '',
        other_party_phone: '', other_party_vehicle_registration: '',
        other_party_address: '', other_party_postcode: '', other_party_insurance_company: '', other_party_policy_number: '',
        immediate_actions_taken: '',
    };
    const [createForm, setCreateForm] = useState(defaultCreateForm);
    const dashcamInputRef = useRef(null);
    const tachographInputRef = useRef(null);
    const imageInputRef = useRef(null);
    const docInputRef = useRef(null);

    const [isDragOverImage, setIsDragOverImage] = useState(false);
    const [isDragOverDoc, setIsDragOverDoc] = useState(false);
    const [isDownloadingImages, setIsDownloadingImages] = useState(false);
    const [isDownloadingReport, setIsDownloadingReport] = useState(false);

    const handleDownloadReport = async () => {
        if (!incidentDetails?.id) return;
        setIsDownloadingReport(true);
        const result = await downloadIncidentReport(incidentDetails.id);
        if (result.status !== 200) {
            showAlert({ message: result.error || 'Failed to download report', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        }
        setIsDownloadingReport(false);
    };

    const handleDownloadAllImages = async () => {
        const images = (incidentDetails.images || []).filter(img => img.image_url);
        if (!images.length) return;
        setIsDownloadingImages(true);
        for (let i = 0; i < images.length; i++) {
            const img = images[i];
            try {
                const response = await fetch(img.image_url);
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = img.file_name || `incident-image-${i + 1}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } catch {
                // skip images that fail to fetch
            }
        }
        setIsDownloadingImages(false);
    };

    const [formData, setFormData] = useState({
        status: 'submitted',
        manager_assigned_to: '',
        management_summary: '',
        corrective_actions: '',
        close_out_notes: '',
        immediate_actions_taken: '',
        dashcam_secured: false,
        tachograph_secured: false,
    });

    const [actionData, setActionData] = useState({
        action_type: '',
        action_status: '',
        note: '',
        visible_to_driver: true,
    });

    const loadIncidents = async () => {
        setIsLoading(true);
        const response = await fetchIncidents({});
        if (response.status === 200) {
            const list = response.incidents || [];
            setIncidents(list);
        } else {
            showAlert({
                message: response.error || 'Failed to load incidents',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }],
            });
        }
        setIsLoading(false);
    };

    const loadIncidentDetails = async (incidentId) => {
        if (!incidentId) {
            setIncidentDetails(null);
            return;
        }

        const response = await fetchIncidentById(incidentId);
        if (response.status === 200 && response.incident) {
            const incident = response.incident;
            setIncidentDetails(incident);
            setFormData({
                status: incident.status || 'submitted',
                manager_assigned_to: incident.manager_assigned_to || '',
                management_summary: incident.management_summary || '',
                corrective_actions: incident.corrective_actions || '',
                close_out_notes: incident.close_out_notes || '',
                immediate_actions_taken: incident.immediate_actions_taken || '',
                dashcam_secured: Boolean(incident.dashcam_secured),
                tachograph_secured: Boolean(incident.tachograph_secured),
            });
            setIsEditingImmediateActions(false);
        } else {
            showAlert({
                message: response.error || 'Failed to load incident details',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }],
            });
        }
    };

    useEffect(() => {
        loadIncidents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (showCreateModal && allVehicles.length === 0) {
            fetch_all_vehicles().then((res) => {
                if (res.status === 200) setAllVehicles(res.data || []);
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCreateModal]);

    const handleCreateIncident = async () => {
        setIsCreating(true);
        const payload = {
            ...createForm,
            vehicle_id: createForm.vehicle_id ? parseInt(createForm.vehicle_id, 10) : null,
        };
        const response = await createIncident(payload);
        setIsCreating(false);
        if (response.status === 201) {
            setShowCreateModal(false);
            setCreateForm(defaultCreateForm);
            await loadIncidents();
            setSelectedIncidentId(response.incident.id);
        } else {
            showAlert({ message: response.error || 'Failed to create incident', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        }
    };

    const canCreate = useMemo(
        () => !!createForm.profile_id && !!createForm.incident_type && !!createForm.incident_at && !!createForm.summary && !!createForm.driver_statement.trim(),
        [createForm]
    );

    useEffect(() => {
        loadIncidentDetails(selectedIncidentId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIncidentId]);

    const isClosed = useMemo(
        () => incidentDetails?.status === 'closed',
        [incidentDetails]
    );
    const canSave = useMemo(
        () => !!incidentDetails && !isSaving && !!formData.manager_assigned_to && !isClosed && canEdit,
        [incidentDetails, isSaving, formData.manager_assigned_to, isClosed, canEdit]
    );
    const managerOptions = useMemo(
        () => (allProfiles || []).filter((profile) => profile?.is_super_admin),
        [allProfiles]
    );

    const handleDashcamUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !incidentDetails?.id) return;
        setIsUploadingDashcam(true);
        setDashcamUploadProgress(0);
        setDashcamUploadPhase('uploading');
        const response = await uploadDashcam(incidentDetails.id, file, (pct) => {
            setDashcamUploadProgress(pct);
            if (pct === 100) setDashcamUploadPhase('saving');
        });
        setIsUploadingDashcam(false);
        setDashcamUploadProgress(0);
        setDashcamUploadPhase('');
        if (response.status === 201) {
            await loadIncidentDetails(incidentDetails.id);
        } else {
            showAlert({ message: response.error || 'Failed to upload dashcam footage', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        }
        if (dashcamInputRef.current) dashcamInputRef.current.value = '';
    };

    const handleTachographUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !incidentDetails?.id) return;
        setIsUploadingTachograph(true);
        const response = await uploadTachograph(incidentDetails.id, file);
        setIsUploadingTachograph(false);
        if (response.status === 201) {
            await loadIncidentDetails(incidentDetails.id);
        } else {
            showAlert({ message: response.error || 'Failed to upload tachograph print', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        }
        if (tachographInputRef.current) tachographInputRef.current.value = '';
    };

    const handleSaveIncident = async () => {
        if (!incidentDetails?.id) return;

        setIsSaving(true);
        const response = await updateIncident(incidentDetails.id, formData);

        if (response.status === 200) {
            showAlert({
                message: 'Incident updated and driver notified.',
                type: 'success',
                buttons: [{ label: 'Close', type: 'success' }],
            });
            await loadIncidents();
            await loadIncidentDetails(incidentDetails.id);
        } else {
            showAlert({
                message: response.error || 'Failed to update incident',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }],
            });
        }
        setIsSaving(false);
    };

    const handleAddAction = async () => {
        if (!incidentDetails?.id) return;

        setIsSaving(true);
        const response = await addIncidentAction(incidentDetails.id, actionData);

        if (response.status === 201) {
            showAlert({
                message: 'Action added and driver notified.',
                type: 'success',
                buttons: [{ label: 'Close', type: 'success' }],
            });
            setActionData({
                action_type: '',
                action_status: '',
                note: '',
                visible_to_driver: true,
            });
            await loadIncidentDetails(incidentDetails.id);
        } else {
            showAlert({
                message: response.error || 'Failed to add incident action',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }],
            });
        }
        setIsSaving(false);
    };

    return (
        <>
        <div style={{ display: 'flex', gap: 20, padding: 16 }}>
            <div style={{ width: '27%', minWidth: 260, maxWidth: 360 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <h4 style={{ margin: 0 }}>Incidents</h4>
                    {canEdit && (
                        <button className="btn btn-primary btn-sm" onClick={() => setShowCreateModal(true)}>+ Create</button>
                    )}
                </div>
                {isLoading && <p>Loading...</p>}
                {!isLoading && incidents.length === 0 && <p>No incidents found.</p>}
                {!isLoading && incidents.length > 0 && (
                    <div style={{ border: `1px solid ${theme === 'dark' ? '#555' : '#ccc'}`, borderRadius: 8, overflow: 'hidden' }}>
                        {incidents.map((incident) => (
                            <div
                                key={incident.id}
                                onClick={() => setSelectedIncidentId(incident.id)}
                                style={{
                                    cursor: 'pointer',
                                    padding: 10,
                                    borderBottom: `1px solid ${theme === 'dark' ? '#444' : '#eee'}`,
                                    background:
                                        selectedIncidentId === incident.id
                                            ? 'rgba(25, 151, 235, 0.15)'
                                            : 'transparent',
                                }}
                            >
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                    {incident.incident_type || incident.id}
                                </div>
                                <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 2 }}>
                                    Driver: {incident?.profiles?.name || 'Unknown'} {incident?.profiles?.surname || ''}
                                </div>
                                <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 2 }}>
                                    Submitted: {formatIncidentDate(incident.submitted_at || incident.reported_at)}
                                </div>
                                <div style={{ fontSize: 12, opacity: 0.8 }}>
                                    Summary: {incident.summary || 'Not provided'}
                                </div>
                                <div style={{ marginTop: 4 }}>
                                    <span style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        padding: '2px 8px',
                                        borderRadius: 12,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                        background: incident.status === 'closed' ? '#d1fae5' : incident.status === 'action_required' ? '#fee2e2' : incident.status === 'under_review' ? '#fef3c7' : '#dbeafe',
                                        color: incident.status === 'closed' ? '#065f46' : incident.status === 'action_required' ? '#991b1b' : incident.status === 'under_review' ? '#92400e' : '#1e40af',
                                    }}>
                                        {(incident.status || 'submitted').replace(/_/g, ' ')}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div style={{ flex: 1 }}>
                {selectedIncidentId && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <h4 style={{ margin: 0 }}>Incident Review</h4>
                        {isClosed && (
                            <button
                                onClick={handleDownloadReport}
                                disabled={isDownloadingReport}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '7px 14px',
                                    borderRadius: 7,
                                    background: isDownloadingReport ? (theme === 'dark' ? '#374151' : '#e5e7eb') : '#1e3a8a',
                                    color: isDownloadingReport ? (theme === 'dark' ? '#9ca3af' : '#6b7280') : '#ffffff',
                                    fontWeight: 600,
                                    fontSize: 13,
                                    border: 'none',
                                    cursor: isDownloadingReport ? 'not-allowed' : 'pointer',
                                    opacity: isDownloadingReport ? 0.7 : 1,
                                    transition: 'opacity 0.2s',
                                }}
                            >
                                <span style={{ fontSize: 15 }}>📄</span>
                                {isDownloadingReport ? 'Generating PDF...' : 'Download Report'}
                            </button>
                        )}
                    </div>
                )}
                {!incidentDetails && !selectedIncidentId && null}

                {incidentDetails && (
                    <>
                        <div style={sectionStyle(theme)}>
                            <div style={sectionTitleStyle(theme)}>Incident Summary</div>
                            <div style={detailGridStyle}>
                                <div>
                                    <strong>Driver:</strong>{' '}
                                    {incidentDetails?.profiles?.name} {incidentDetails?.profiles?.surname}
                                </div>
                                <div>
                                    <strong>Submitted:</strong> {formatIncidentDate(incidentDetails.submitted_at || incidentDetails.reported_at)}
                                </div>
                                <div>
                                    <strong>Incident At:</strong> {formatIncidentDate(incidentDetails.incident_at)}
                                </div>
                                <div>
                                    <strong>Summary:</strong> {incidentDetails.summary || 'Not provided'}
                                </div>
                            </div>
                        </div>

                        <div style={sectionStyle(theme)}>
                            <div style={sectionTitleStyle(theme)}>Our Details</div>
                            <div style={detailGridStyle}>
                                <div>
                                    <strong>Vehicle:</strong> {incidentDetails?.vehicles?.reg || 'Not provided'}
                                </div>
                                <div>
                                    <strong>Immediate Actions Taken:</strong> {incidentDetails.immediate_actions_taken || 'Not provided'}
                                </div>
                            </div>
                        </div>

                        {/* Dashcam Footage */}
                        <div style={sectionStyle(theme)}>
                            <div style={sectionTitleStyle(theme)}>Dashcam Footage</div>
                            <label style={{ ...getCheckboxCardStyle(theme, formData.dashcam_secured), display: 'inline-flex', marginBottom: 12 }}>
                                <input
                                    type="checkbox"
                                    checked={formData.dashcam_secured}
                                    disabled={isClosed || !canEdit || (incidentDetails.dashcam_footage || []).length > 0}
                                    onChange={(e) => setFormData({ ...formData, dashcam_secured: e.target.checked })}
                                />
                                <span><strong>Dashcam Secured</strong></span>
                            </label>
                            {formData.dashcam_secured && (
                                <div style={{ marginTop: 8 }}>
                                    {(incidentDetails.dashcam_footage || []).length > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {incidentDetails.dashcam_footage.map((f) => (
                                                <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, border: `1px solid ${theme === 'dark' ? '#444' : '#d1d5db'}`, background: theme === 'dark' ? '#1f2937' : '#f9fafb' }}>
                                                    <span style={{ fontSize: 20 }}>🎥</span>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name}</div>
                                                        {f.file_size && <div style={{ fontSize: 11, opacity: 0.6 }}>{(f.file_size / 1048576).toFixed(1)} MB</div>}
                                                    </div>
                                                    {f.url && (
                                                        <a href={f.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">View</a>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (!isClosed && canEdit) ? (
                                        <div>
                                            <input
                                                ref={dashcamInputRef}
                                                type="file"
                                                accept="video/mp4"
                                                style={{ display: 'none' }}
                                                onChange={handleDashcamUpload}
                                            />
                                            {isUploadingDashcam ? (
                                                <div style={{ minWidth: 260 }}>
                                                    {dashcamUploadPhase === 'uploading' ? (
                                                        <>
                                                            <div style={{ fontSize: 12, marginBottom: 4, opacity: 0.7 }}>Uploading to server... {dashcamUploadProgress}%</div>
                                                            <div className="progress" style={{ height: 8, borderRadius: 4 }}>
                                                                <div
                                                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                                                    style={{ width: `${dashcamUploadProgress}%`, transition: 'width 0.2s ease' }}
                                                                />
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div style={{ fontSize: 12, marginBottom: 4, opacity: 0.7 }}>Saving to storage...</div>
                                                            <div className="progress" style={{ height: 8, borderRadius: 4 }}>
                                                                <div
                                                                    className="progress-bar progress-bar-striped progress-bar-animated bg-warning"
                                                                    style={{ width: '100%' }}
                                                                />
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            ) : (
                                                <button
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={() => dashcamInputRef.current?.click()}
                                                >
                                                    ⬆ Upload MP4
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: 13, opacity: 0.6 }}>No footage uploaded.</div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Tachograph Print */}
                        <div style={sectionStyle(theme)}>
                            <div style={sectionTitleStyle(theme)}>Tachograph Print</div>
                            <label style={{ ...getCheckboxCardStyle(theme, formData.tachograph_secured), display: 'inline-flex', marginBottom: 12 }}>
                                <input
                                    type="checkbox"
                                    checked={formData.tachograph_secured}
                                    disabled={isClosed || !canEdit || (incidentDetails.tachograph_prints || []).length > 0}
                                    onChange={(e) => setFormData({ ...formData, tachograph_secured: e.target.checked })}
                                />
                                <span><strong>Tachograph Secured</strong></span>
                            </label>
                            {formData.tachograph_secured && (
                                <div style={{ marginTop: 8 }}>
                                    {(incidentDetails.tachograph_prints || []).length > 0 ? (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                            {incidentDetails.tachograph_prints.map((p) => (
                                                <a key={p.id} href={p.url} target="_blank" rel="noreferrer" style={{ display: 'block', flexShrink: 0 }}>
                                                    {p.url && <img src={p.url} alt={p.file_name} style={{ width: 140, height: 140, objectFit: 'cover', borderRadius: 8, border: `1px solid ${theme === 'dark' ? '#555' : '#d1d5db'}` }} />}
                                                </a>
                                            ))}
                                        </div>
                                    ) : (!isClosed && canEdit) ? (
                                        <div>
                                            <input
                                                ref={tachographInputRef}
                                                type="file"
                                                accept="image/jpeg,image/png"
                                                style={{ display: 'none' }}
                                                onChange={handleTachographUpload}
                                            />
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => tachographInputRef.current?.click()}
                                                disabled={isUploadingTachograph}
                                            >
                                                {isUploadingTachograph ? 'Uploading...' : '⬆ Upload Image'}
                                            </button>
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: 13, opacity: 0.6 }}>No print uploaded.</div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div style={sectionStyle(theme)}>
                            <div style={sectionTitleStyle(theme)}>3rd Party Details</div>
                            <div style={detailGridStyle}>
                                <div><strong>Name:</strong> {incidentDetails.other_party_name || 'Not provided'}</div>
                                <div><strong>Company:</strong> {incidentDetails.other_party_company || 'Not provided'}</div>
                                <div><strong>Phone:</strong> {incidentDetails.other_party_phone || 'Not provided'}</div>
                                <div><strong>Vehicle:</strong> {incidentDetails.other_party_vehicle_registration || 'Not provided'}</div>
                                <div><strong>Address:</strong> {incidentDetails.other_party_address || 'Not provided'}</div>
                                <div><strong>Postcode:</strong> {incidentDetails.other_party_postcode || 'Not provided'}</div>
                                <div><strong>Insurance Company:</strong> {incidentDetails.other_party_insurance_company || 'Not provided'}</div>
                                <div><strong>Policy Number:</strong> {incidentDetails.other_party_policy_number || 'Not provided'}</div>
                            </div>
                        </div>

                        <div style={sectionStyle(theme)}>
                            <div style={sectionTitleStyle(theme)}>Injuries</div>
                            <div style={detailGridStyle}>
                                <div>
                                    <strong>Has Injury:</strong> {formatBoolean(incidentDetails.has_injury)}
                                </div>
                                <div>
                                    <strong>Injured Person:</strong> {incidentDetails.injured_person_name || 'Not provided'}
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <strong>Injury Details:</strong> {incidentDetails.injury_details || 'Not provided'}
                                </div>
                            </div>
                        </div>

                        <div style={sectionStyle(theme)}>
                            <div style={sectionTitleStyle(theme)}>Police / Ambulance</div>
                            <div style={detailGridStyle}>
                                <div>
                                    <strong>Police Attended:</strong> {formatBoolean(incidentDetails.police_attended)}
                                </div>
                                <div>
                                    <strong>Police Reference:</strong> {incidentDetails.police_reference || 'Not provided'}
                                </div>
                                <div>
                                    <strong>Emergency Services Called:</strong> {formatBoolean(incidentDetails.emergency_services_called)}
                                </div>
                            </div>
                        </div>

                        <div style={sectionStyle(theme)}>
                            <div style={sectionTitleStyle(theme)}>Location</div>
                            <div style={detailGridStyle}>
                                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'baseline' }}>
                                    <span><strong>Location:</strong> {incidentDetails.location_text || 'Not provided'}</span>
                                    {incidentDetails.latitude != null && incidentDetails.longitude != null && (
                                        <a
                                            href={`https://www.google.com/maps/search/?api=1&query=${incidentDetails.latitude},${incidentDetails.longitude}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{ color: theme === 'dark' ? '#60a5fa' : '#2563eb', textDecoration: 'none', fontWeight: 600, fontSize: 13 }}
                                        >
                                            📍 {incidentDetails.latitude}, {incidentDetails.longitude}
                                        </a>
                                    )}
                                </div>
                                <div>
                                    <strong>Weather Conditions:</strong> {incidentDetails.weather_conditions || 'Not provided'}
                                </div>
                                <div>
                                    <strong>Road Conditions:</strong> {incidentDetails.road_conditions || 'Not provided'}
                                </div>
                                <div>
                                    <strong>Witness Details:</strong> {incidentDetails.witness_details || 'Not provided'}
                                </div>
                            </div>
                        </div>

                        <div style={sectionStyle(theme)}>
                            <div style={sectionTitleStyle(theme)}>Driver Statement</div>
                            <div
                                style={{
                                    padding: 12,
                                    borderRadius: 8,
                                    border: `1px solid ${theme === 'dark' ? '#555' : '#d1d5db'}`,
                                    background: theme === 'dark' ? '#1f2937' : '#ffffff',
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.5,
                                }}
                            >
                                {incidentDetails.driver_statement || 'Not provided'}
                            </div>
                        </div>

                        <div style={sectionStyle(theme)}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                    <div style={sectionTitleStyle(theme)}>Incident Images</div>
                                    {(incidentDetails.images || []).filter(img => img.image_url).length > 0 && (
                                        <button
                                            onClick={handleDownloadAllImages}
                                            disabled={isDownloadingImages}
                                            style={{
                                                padding: '5px 12px',
                                                borderRadius: 6,
                                                background: theme === 'dark' ? '#374151' : '#e5e7eb',
                                                color: theme === 'dark' ? '#d1d5db' : '#374151',
                                                fontSize: 13,
                                                fontWeight: 500,
                                                border: `1px solid ${theme === 'dark' ? '#555' : '#d1d5db'}`,
                                                cursor: isDownloadingImages ? 'not-allowed' : 'pointer',
                                                opacity: isDownloadingImages ? 0.6 : 1,
                                            }}
                                        >
                                            {isDownloadingImages ? 'Downloading...' : '⬇ Download All'}
                                        </button>
                                    )}
                                </div>
                                <input
                                    ref={imageInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    style={{ display: 'none' }}
                                    disabled={isClosed || !canEdit || isUploadingImage}
                                    onChange={async (e) => {
                                        const files = Array.from(e.target.files);
                                        if (!files.length) return;
                                        setIsUploadingImage(true);
                                        for (const file of files) {
                                            await uploadIncidentImage(incidentDetails.id, file);
                                        }
                                        await loadIncidentDetails(incidentDetails.id);
                                        setIsUploadingImage(false);
                                        e.target.value = '';
                                    }}
                                />
                                {!isClosed && canEdit ? (
                                <div
                                    onDragOver={(e) => { e.preventDefault(); if (!isUploadingImage) setIsDragOverImage(true); }}
                                    onDragLeave={(e) => { e.preventDefault(); setIsDragOverImage(false); }}
                                    onDrop={async (e) => {
                                        e.preventDefault();
                                        setIsDragOverImage(false);
                                        if (isUploadingImage) return;
                                        const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
                                        if (!files.length) return;
                                        setIsUploadingImage(true);
                                        for (const file of files) {
                                            await uploadIncidentImage(incidentDetails.id, file);
                                        }
                                        await loadIncidentDetails(incidentDetails.id);
                                        setIsUploadingImage(false);
                                    }}
                                    onClick={() => !isUploadingImage && imageInputRef.current?.click()}
                                    style={{
                                        border: `2px dashed ${isDragOverImage ? '#3b82f6' : (theme === 'dark' ? '#555' : '#d1d5db')}`,
                                        borderRadius: 8,
                                        padding: '14px 12px',
                                        textAlign: 'center',
                                        cursor: isUploadingImage ? 'not-allowed' : 'pointer',
                                        background: isDragOverImage
                                            ? (theme === 'dark' ? '#1e3a5f' : '#eff6ff')
                                            : (theme === 'dark' ? '#1f2937' : '#f9fafb'),
                                        transition: 'all 0.2s ease',
                                        marginBottom: 12,
                                        opacity: isUploadingImage ? 0.6 : 1,
                                    }}
                                >
                                    <div style={{ fontSize: 22, marginBottom: 4 }}>🖼️</div>
                                    <div style={{ fontSize: 13, color: theme === 'dark' ? '#d1d5db' : '#374151', fontWeight: 500 }}>
                                        {isUploadingImage ? 'Uploading...' : (isDragOverImage ? 'Drop images here' : 'Drop images here or click to select')}
                                    </div>
                                    <div style={{ fontSize: 11, color: theme === 'dark' ? '#9ca3af' : '#6b7280', marginTop: 2 }}>
                                        Supports JPG, PNG, GIF and other image formats
                                    </div>
                                </div>
                                ) : null}
                                {(incidentDetails.images || []).filter(img => img.image_url).length === 0 ? (
                                    <div style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: 13 }}>No images uploaded yet.</div>
                                ) : (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                        {(incidentDetails.images || []).filter(img => img.image_url).map((img, idx) => (
                                            <a
                                                key={img.id || idx}
                                                href={img.image_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ display: 'block', flexShrink: 0 }}
                                            >
                                                <img
                                                    src={img.image_url}
                                                    alt={img.caption || img.file_name || `Incident image ${idx + 1}`}
                                                    style={{
                                                        width: 140,
                                                        height: 140,
                                                        objectFit: 'cover',
                                                        borderRadius: 8,
                                                        border: `1px solid ${theme === 'dark' ? '#555' : '#d1d5db'}`,
                                                    }}
                                                />
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>

                        <div style={sectionStyle(theme)}>
                            <div style={{ marginBottom: 10 }}>
                                <div style={sectionTitleStyle(theme)}>Supporting Documents</div>
                            </div>
                            <input
                                ref={docInputRef}
                                type="file"
                                accept=".pdf,application/pdf"
                                multiple
                                style={{ display: 'none' }}
                                disabled={isClosed || !canEdit || isUploadingDocument}
                                onChange={async (e) => {
                                    const files = Array.from(e.target.files).filter(f => f.type.includes('pdf') || f.name.toLowerCase().endsWith('.pdf'));
                                    if (!files.length) return;
                                    setIsUploadingDocument(true);
                                    for (const file of files) {
                                        await uploadSupportingDocument(incidentDetails.id, file);
                                    }
                                    await loadIncidentDetails(incidentDetails.id);
                                    setIsUploadingDocument(false);
                                    e.target.value = '';
                                }}
                            />
                            {!isClosed && canEdit ? (
                            <div
                                onDragOver={(e) => { e.preventDefault(); if (!isUploadingDocument) setIsDragOverDoc(true); }}
                                onDragLeave={(e) => { e.preventDefault(); setIsDragOverDoc(false); }}
                                onDrop={async (e) => {
                                    e.preventDefault();
                                    setIsDragOverDoc(false);
                                    if (isUploadingDocument) return;
                                    const files = Array.from(e.dataTransfer.files).filter(f => f.type.includes('pdf') || f.name.toLowerCase().endsWith('.pdf'));
                                    if (!files.length) {
                                        showAlert({ message: 'Only PDF files are allowed for supporting documents', type: 'warning', buttons: [{ label: 'OK', type: 'primary' }] });
                                        return;
                                    }
                                    setIsUploadingDocument(true);
                                    for (const file of files) {
                                        await uploadSupportingDocument(incidentDetails.id, file);
                                    }
                                    await loadIncidentDetails(incidentDetails.id);
                                    setIsUploadingDocument(false);
                                }}
                                onClick={() => !isUploadingDocument && docInputRef.current?.click()}
                                style={{
                                    border: `2px dashed ${isDragOverDoc ? '#3b82f6' : (theme === 'dark' ? '#555' : '#d1d5db')}`,
                                    borderRadius: 8,
                                    padding: '14px 12px',
                                    textAlign: 'center',
                                    cursor: isUploadingDocument ? 'not-allowed' : 'pointer',
                                    background: isDragOverDoc
                                        ? (theme === 'dark' ? '#1e3a5f' : '#eff6ff')
                                        : (theme === 'dark' ? '#1f2937' : '#f9fafb'),
                                    transition: 'all 0.2s ease',
                                    marginBottom: 12,
                                    opacity: isUploadingDocument ? 0.6 : 1,
                                }}
                            >
                                <div style={{ fontSize: 22, marginBottom: 4 }}>📄</div>
                                <div style={{ fontSize: 13, color: theme === 'dark' ? '#d1d5db' : '#374151', fontWeight: 500 }}>
                                    {isUploadingDocument ? 'Uploading...' : (isDragOverDoc ? 'Drop PDF files here' : 'Drop PDF files here or click to select')}
                                </div>
                                <div style={{ fontSize: 11, color: theme === 'dark' ? '#9ca3af' : '#6b7280', marginTop: 2 }}>
                                    PDF files only, up to 10MB each
                                </div>
                            </div>
                            ) : null}
                            {(incidentDetails.supporting_documents || []).filter(doc => doc.download_url).length === 0 ? (
                                <div style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: 13 }}>No supporting documents uploaded yet.</div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {(incidentDetails.supporting_documents || []).filter(doc => doc.download_url).map((doc, idx) => (
                                        <div key={doc.id || idx} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '8px 12px',
                                            background: theme === 'dark' ? '#374151' : '#f3f4f6',
                                            borderRadius: 6,
                                            border: `1px solid ${theme === 'dark' ? '#555' : '#d1d5db'}`,
                                        }}>
                                            <div style={{ marginRight: 12, fontSize: 18 }}>📄</div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 500, fontSize: 14, color: theme === 'dark' ? '#e5e7eb' : '#374151' }}>
                                                    {doc.file_name}
                                                </div>
                                                <div style={{ fontSize: 12, color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                                                    {doc.file_size ? `${Math.round(doc.file_size / 1024)} KB` : 'PDF Document'}
                                                </div>
                                            </div>
                                            <a
                                                href={doc.download_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{
                                                    padding: '4px 8px',
                                                    background: '#3b82f6',
                                                    color: 'white',
                                                    textDecoration: 'none',
                                                    borderRadius: 4,
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                }}
                                            >
                                                Download
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <label>
                                Status
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    style={getInputStyle(theme, isClosed || !canEdit)}
                                disabled={isClosed || !canEdit}
                                >
                                    {STATUS_OPTIONS.map((status) => (
                                        <option key={status} value={status}>
                                            {status}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                Assigned Manager
                                <select
                                    value={formData.manager_assigned_to}
                                    onChange={(e) => setFormData({ ...formData, manager_assigned_to: e.target.value })}
                                    disabled={isClosed || !canEdit}
                                    style={getInputStyle(theme, isClosed || !canEdit)}
                                >
                                    <option value="">Unassigned</option>
                                    {managerOptions.map((manager) => (
                                        <option key={manager.id} value={manager.id}>
                                            {manager.name} {manager.surname}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <label style={{ display: 'block', marginTop: 12 }}>
                            Management Summary
                            <textarea
                                value={formData.management_summary}
                                onChange={(e) => setFormData({ ...formData, management_summary: e.target.value })}
                                rows={3}
                                disabled={isClosed || !canEdit}
                                style={getInputStyle(theme, isClosed || !canEdit)}
                            />
                        </label>

                        <label style={{ display: 'block', marginTop: 12 }}>
                            Corrective Actions
                            <textarea
                                value={formData.corrective_actions}
                                onChange={(e) => setFormData({ ...formData, corrective_actions: e.target.value })}
                                rows={3}
                                disabled={isClosed || !canEdit}
                                style={getInputStyle(theme, isClosed || !canEdit)}
                            />
                        </label>

                        <div style={{ display: 'block', marginTop: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <label style={{ margin: 0 }}>Immediate Actions Taken</label>
                                {!isClosed && canEdit && (
                                    <button
                                        type="button"
                                        onClick={() => setIsEditingImmediateActions((current) => !current)}
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: 6,
                                            border: `1px solid ${theme === 'dark' ? '#666' : '#cfd6de'}`,
                                            background: theme === 'dark' ? '#374151' : '#f3f4f6',
                                            color: theme === 'dark' ? '#f3f4f6' : '#111827',
                                        }}
                                    >
                                        {isEditingImmediateActions ? 'Lock' : 'Edit'}
                                    </button>
                                )}
                            </div>
                            <textarea
                                value={formData.immediate_actions_taken}
                                onChange={(e) => setFormData({ ...formData, immediate_actions_taken: e.target.value })}
                                rows={2}
                                disabled={isClosed || !canEdit || !isEditingImmediateActions}
                                style={getInputStyle(theme, isClosed || !canEdit || !isEditingImmediateActions)}
                            />
                        </div>

                        <label style={{ display: 'block', marginTop: 12 }}>
                            Close Out Notes
                            <textarea
                                value={formData.close_out_notes}
                                onChange={(e) => setFormData({ ...formData, close_out_notes: e.target.value })}
                                rows={2}
                                disabled={isClosed || !canEdit}
                                style={getInputStyle(theme, isClosed || !canEdit)}
                            />
                        </label>

                        {canEdit && (
                            <button
                                className="btn btn-primary"
                                onClick={handleSaveIncident}
                                disabled={!canSave}
                                style={{ marginTop: 12 }}
                            >
                                {isSaving ? 'Saving...' : 'Save Incident Update'}
                            </button>
                        )}

                        <hr style={{ margin: '20px 0' }} />

                        <h5>Add Action</h5>
                        {isClosed ? (
                            <div style={{ padding: '8px 12px', borderRadius: 6, background: theme === 'dark' ? '#1f2937' : '#f3f4f6', color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: 13, marginBottom: 12 }}>
                                This incident is closed. No further actions can be added.
                            </div>
                        ) : !canEdit ? (
                            <div style={{ padding: '8px 12px', borderRadius: 6, background: theme === 'dark' ? '#1f2937' : '#f3f4f6', color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: 13, marginBottom: 12 }}>
                                You do not have permissions to add actions to this incident.
                            </div>
                        ) : null}
                        {!isClosed && canEdit && (<>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <label>
                                Action Type
                                <select
                                    value={actionData.action_type}
                                    onChange={(e) => setActionData({ ...actionData, action_type: e.target.value })}
                                    style={getInputStyle(theme)}
                                >
                                    <option value="">Select type...</option>
                                    <option value="Investigation">Investigation</option>
                                    <option value="Warning Issued">Warning Issued</option>
                                    <option value="Training Required">Training Required</option>
                                    <option value="Disciplinary">Disciplinary</option>
                                    <option value="No Action Required">No Action Required</option>
                                    <option value="Other">Other</option>
                                </select>
                            </label>

                            <label>
                                Action Status
                                <select
                                    value={actionData.action_status}
                                    onChange={(e) => setActionData({ ...actionData, action_status: e.target.value })}
                                    style={getInputStyle(theme)}
                                >
                                    <option value="">Select status...</option>
                                    <option value="Pending">Pending</option>
                                    <option value="In Progress">In Progress</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Closed">Closed</option>
                                </select>
                            </label>
                        </div>

                        <label style={{ display: 'block', marginTop: 12 }}>
                            Note
                            <textarea
                                value={actionData.note}
                                onChange={(e) => setActionData({ ...actionData, note: e.target.value })}
                                rows={3}
                                style={getInputStyle(theme)}
                            />
                        </label>

                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                            <input
                                type="checkbox"
                                checked={actionData.visible_to_driver}
                                onChange={(e) => setActionData({ ...actionData, visible_to_driver: e.target.checked })}
                            />
                            Visible to driver
                        </label>

                        </>)}
                        {!isClosed && canEdit && (
                            <div>
                                <button
                                    className="btn btn-success"
                                    onClick={handleAddAction}
                                    disabled={isSaving || !actionData.action_type || !actionData.action_status || !actionData.note.trim()}
                                    style={{ marginTop: 12 }}
                                >
                                    {isSaving ? 'Saving...' : 'Add Action'}
                                </button>
                            </div>
                        )}

                        <hr style={{ margin: '20px 0' }} />
                        <h5>Actions</h5>
                        {(incidentDetails.actions || []).length === 0 && <p>No actions yet.</p>}
                        {(incidentDetails.actions || []).map((action) => (
                            <div
                                key={action.id}
                                style={{
                                    marginBottom: 10,
                                    padding: 10,
                                    border: `1px solid ${theme === 'dark' ? '#555' : '#ddd'}`,
                                    borderRadius: 8,
                                }}
                            >
                                <div style={{ fontWeight: 600 }}>{action.action_type}</div>
                                <div style={{ fontSize: 12, opacity: 0.8 }}>{action.created_at}</div>
                                {action.note && <div style={{ marginTop: 4 }}>{action.note}</div>}
                            </div>
                        ))}
                    </>
                )}
            </div>
        </div>
        {showCreateModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }}>
                <div style={{ background: theme === 'dark' ? '#1f2937' : '#ffffff', borderRadius: 12, width: '100%', maxWidth: 680, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h5 style={{ margin: 0 }}>Create Incident</h5>
                        <button className="btn-close" onClick={() => { setShowCreateModal(false); setCreateForm(defaultCreateForm); }} />
                    </div>

                    {/* Section: Core Details */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.5, marginBottom: 10 }}>Incident Details</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                Driver / Person Involved <span style={{ color: '#ef4444' }}>*</span>
                                <select value={createForm.profile_id} onChange={(e) => setCreateForm({ ...createForm, profile_id: e.target.value })} style={getInputStyle(theme, false)}>
                                    <option value="">Select...</option>
                                    {(allProfiles || []).filter((p) => p.group && ['DRIVER', 'ADMIN'].includes(p.group.toUpperCase())).map((p) => (
                                        <option key={p.id} value={p.id}>{p.name} {p.surname}</option>
                                    ))}
                                </select>
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                Vehicle
                                <select value={createForm.vehicle_id} onChange={(e) => setCreateForm({ ...createForm, vehicle_id: e.target.value })} style={getInputStyle(theme, false)}>
                                    <option value="">None / Unknown</option>
                                    {allVehicles.filter((v) => v.status === 'active' && (v.type === 'UNIT' || v.type === 'RIGID')).sort((a, b) => a.reg.localeCompare(b.reg)).map((v) => (
                                        <option key={v.id} value={v.id}>{v.reg}</option>
                                    ))}
                                </select>
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                Incident Type <span style={{ color: '#ef4444' }}>*</span>
                                <select value={createForm.incident_type} onChange={(e) => setCreateForm({ ...createForm, incident_type: e.target.value })} style={getInputStyle(theme, false)}>
                                    <option value="">Select...</option>
                                    <option value="vehicle_accident">Vehicle Accident (RTC)</option>
                                    <option value="personal_injury">Personal Injury</option>
                                    <option value="vehicle_damage">Vehicle Damage</option>
                                    <option value="property_damage">Property Damage</option>
                                    <option value="near_miss">Near Miss</option>
                                    <option value="other">Other</option>
                                </select>
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                Incident Date &amp; Time <span style={{ color: '#ef4444' }}>*</span>
                                <input type="datetime-local" value={createForm.incident_at} onChange={(e) => setCreateForm({ ...createForm, incident_at: e.target.value })} style={getInputStyle(theme, false)} />
                            </label>
                        </div>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, marginTop: 12 }}>
                            Summary <span style={{ color: '#ef4444' }}>*</span>
                            <input type="text" value={createForm.summary} onChange={(e) => setCreateForm({ ...createForm, summary: e.target.value })} placeholder="Brief one-line description" style={getInputStyle(theme, false)} />
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, marginTop: 12 }}>
                            Location
                            <input type="text" value={createForm.location_text} onChange={(e) => setCreateForm({ ...createForm, location_text: e.target.value })} placeholder="e.g. A14 Junction 21" style={getInputStyle(theme, false)} />
                        </label>
                    </div>

                    {/* Section: Statement */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.5, marginBottom: 10 }}>Driver Statement</div>
                        <textarea
                            value={createForm.driver_statement}
                            onChange={(e) => setCreateForm({ ...createForm, driver_statement: e.target.value })}
                            placeholder="Full account of what happened..."
                            rows={4}
                            style={{ ...getInputStyle(theme, false), resize: 'vertical' }}
                        />
                    </div>

                    {/* Section: Immediate Actions */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.5, marginBottom: 10 }}>Immediate Actions Taken</div>
                        <textarea
                            value={createForm.immediate_actions_taken}
                            onChange={(e) => setCreateForm({ ...createForm, immediate_actions_taken: e.target.value })}
                            placeholder="Describe any immediate actions taken after the incident..."
                            rows={3}
                            style={{ ...getInputStyle(theme, false), resize: 'vertical' }}
                        />
                    </div>

                    {/* Section: Conditions */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.5, marginBottom: 10 }}>Conditions</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                Weather Conditions
                                <select value={createForm.weather_conditions} onChange={(e) => setCreateForm({ ...createForm, weather_conditions: e.target.value })} style={getInputStyle(theme, false)}>
                                    <option value="">Unknown</option>
                                    <option value="Clear">Clear</option>
                                    <option value="Rain">Rain</option>
                                    <option value="Heavy Rain">Heavy Rain</option>
                                    <option value="Fog">Fog</option>
                                    <option value="Snow / Ice">Snow / Ice</option>
                                    <option value="Windy">Windy</option>
                                    <option value="Other">Other</option>
                                </select>
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                Road Conditions
                                <select value={createForm.road_conditions} onChange={(e) => setCreateForm({ ...createForm, road_conditions: e.target.value })} style={getInputStyle(theme, false)}>
                                    <option value="">Unknown</option>
                                    <option value="Dry">Dry</option>
                                    <option value="Wet">Wet</option>
                                    <option value="Icy">Icy</option>
                                    <option value="Flooded">Flooded</option>
                                    <option value="Roadworks">Roadworks</option>
                                    <option value="Other">Other</option>
                                </select>
                            </label>
                        </div>
                    </div>

                    {/* Section: Injuries & Emergency */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.5, marginBottom: 10 }}>Injuries &amp; Emergency Services</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                                <input type="checkbox" checked={createForm.has_injury} onChange={(e) => setCreateForm({ ...createForm, has_injury: e.target.checked })} />
                                Has Injury
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                                <input type="checkbox" checked={createForm.emergency_services_called} onChange={(e) => setCreateForm({ ...createForm, emergency_services_called: e.target.checked })} />
                                Emergency Services
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                                <input type="checkbox" checked={createForm.police_attended} onChange={(e) => setCreateForm({ ...createForm, police_attended: e.target.checked })} />
                                Police Attended
                            </label>
                        </div>
                        {createForm.has_injury && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                    Injured Person Name
                                    <input type="text" value={createForm.injured_person_name} onChange={(e) => setCreateForm({ ...createForm, injured_person_name: e.target.value })} style={getInputStyle(theme, false)} />
                                </label>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                    Injury Details
                                    <input type="text" value={createForm.injury_details} onChange={(e) => setCreateForm({ ...createForm, injury_details: e.target.value })} style={getInputStyle(theme, false)} />
                                </label>
                            </div>
                        )}
                        {createForm.police_attended && (
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, marginBottom: 12 }}>
                                Police Reference
                                <input type="text" value={createForm.police_reference} onChange={(e) => setCreateForm({ ...createForm, police_reference: e.target.value })} style={getInputStyle(theme, false)} />
                            </label>
                        )}
                    </div>

                    {/* Section: Third Party */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.5, marginBottom: 10 }}>Third Party</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                Name
                                <input type="text" value={createForm.other_party_name} onChange={(e) => setCreateForm({ ...createForm, other_party_name: e.target.value })} style={getInputStyle(theme, false)} />
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                Company
                                <input type="text" value={createForm.other_party_company} onChange={(e) => setCreateForm({ ...createForm, other_party_company: e.target.value })} style={getInputStyle(theme, false)} />
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                Phone
                                <input type="text" value={createForm.other_party_phone} onChange={(e) => setCreateForm({ ...createForm, other_party_phone: e.target.value })} style={getInputStyle(theme, false)} />
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                Vehicle Registration
                                <input type="text" value={createForm.other_party_vehicle_registration} onChange={(e) => setCreateForm({ ...createForm, other_party_vehicle_registration: e.target.value })} style={getInputStyle(theme, false)} />
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                Address
                                <input type="text" value={createForm.other_party_address} onChange={(e) => setCreateForm({ ...createForm, other_party_address: e.target.value })} style={getInputStyle(theme, false)} />
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                Postcode
                                <input type="text" value={createForm.other_party_postcode} onChange={(e) => setCreateForm({ ...createForm, other_party_postcode: e.target.value })} style={getInputStyle(theme, false)} />
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                Insurance Company
                                <input type="text" value={createForm.other_party_insurance_company} onChange={(e) => setCreateForm({ ...createForm, other_party_insurance_company: e.target.value })} style={getInputStyle(theme, false)} />
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                                Policy Number
                                <input type="text" value={createForm.other_party_policy_number} onChange={(e) => setCreateForm({ ...createForm, other_party_policy_number: e.target.value })} style={getInputStyle(theme, false)} />
                            </label>
                        </div>
                    </div>

                    {/* Section: Witness */}
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.5, marginBottom: 10 }}>Witness Details</div>
                        <textarea
                            value={createForm.witness_details}
                            onChange={(e) => setCreateForm({ ...createForm, witness_details: e.target.value })}
                            placeholder="Names, contact details, statements..."
                            rows={3}
                            style={{ ...getInputStyle(theme, false), resize: 'vertical' }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                        <button className="btn btn-outline-secondary" onClick={() => { setShowCreateModal(false); setCreateForm(defaultCreateForm); }} disabled={isCreating}>Cancel</button>
                        <button className="btn btn-primary" onClick={handleCreateIncident} disabled={!canCreate || isCreating}>
                            {isCreating ? 'Creating...' : 'Create Incident'}
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default IncidentsManagement;
